import { Action } from 'redux';
import {
    AdvancedOptions,
    AutomationTrigger,
    Campaign,
    CampaignChannel,
    CampaignDateFilterBy,
    CampaignSimpleStats,
    CampaignStatus,
    CampaignType,
    CampaignTypeFilter,
    ChannelChartData,
    InProgressChannelStats,
    IntervalType,
    LocationDetails,
} from '@dev/ee-api/ee-api-models';

import { ExportAutomationParams, ExportCampaignsParams } from '@/services/ElasticApi';
import { ReportsState } from '@/store/reports/types';
export interface CampaignsInterface {
    loading: boolean;
    campaignsNamesLoading?: boolean;
    byTarget?: CampaignChannel[];
    // Overall campaigns count
    statusCount?: CampaignStatusCounts;
    // Current page's Campaigns colection
    collection?: Campaign[];
    // current pagination settings (activePage, perPage, search)
    pagination?: PaginationInterface;
    // Campaigns checked in campaigns table
    checkedCampaigns?: string[];
    pickedCampaign?: PickedCampaignInterface;
    abSplitHeaderControls: AbSplitHeaderControlsInterface;
    pickedContactEmail: string;
    currentPreview?: CurrentPreviewInterface;
    inProgressCampaign?: InProgressChannelStats;
    locationCountryStats?: LocationCountryStatsInterface;
    deleteDialog?: {
        isOpened: boolean;
        campaignNames: string[];
        type: CampaignTypeFilter;
    };
    copyCampaignsLoading?: boolean;
    copiedCampaign?: Campaign;
    simpleStatsAllData?: CampaignSimpleStats;
    campaignsList?: Campaign[];
    isCampaignCreatorOpen?: boolean;
    contactsList?: string;
    campaignsNames?: string[];
    templateName?: string;
}
export interface LocationStatsByCountryIsoInterface {
    countryiso: string;
    name: string;
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
    sortDescending?: boolean;
}

export interface AbSplitHeaderControlsInterface {
    displayMode: AbSplitDisplayMode;
    currentTemplate: string;
}

export enum AbSplitDisplayMode {
    Winner = 'Winner',
    TestResult = 'TestResult',
}

export interface CurrentPreviewInterface {
    isTemplateDeleted?: boolean;
    campaign?: Campaign;
    templates?: CampaignTemplateInterface[];
}

export interface CampaignTemplateInterface {
    name: string;
    backendName: string;
    channelName: string;
    html: string;
}

export interface PickedCampaignInterface {
    isChartDataLoading: boolean;
    campaign: Campaign;
    simpleStats?: CampaignSimpleStats;
    templateLabels?: {
        [key: string]: string;
    };
    clicksAndOpensChartData?: ChannelChartData[];
    automationsTrigger?: AutomationTrigger;
}

export interface PaginationInterface {
    activePage: number;
    perPage: number;
    search?: string;
    to?: string;
    from?: string;
    status?: CampaignStatus[];
    type?: CampaignTypeFilter[];
}

export interface CampaignsByTargetInterface {
    name: string;
    limit?: number;
    offset?: number;
    nameLike?: string;
}

export interface UrlSearchParams {
    page?: number;
    perPage?: number;
    search?: string;
    from?: string;
    to?: string;
    status?: CampaignStatus[];
}

export interface CampaignStatusCounts {
    total: number;
    active: number;
    cancelled: number;
    completed: number;
    deleted: number;
    draft: number;
    paused: number;
    processing: number;
    sending: number;
}

export interface CampaignReportGraph {
    Date: string;
    Clicked?: number;
    Opened?: number;
}

export interface AbSplitLineChartData {
    Date: string;
    [key: string]: string | number;
}

export enum ClicksOrOpens {
    Clicks = 'clicks',
    Opens = 'opens',
}

export enum DropdownOptions {
    ViewDetails = 'viewdetails',
    ViewReport = 'viewreport',
    CopyCampaign = 'copycampaign',
    Delete = 'delete',
    Pause = 'pause',
    Edit = 'edit',
    CancelSending = 'cancelsending',
    Reactivate = 'reactivate',
}

export enum CampaignTabName {
    Autoresponders = 'autoresponders',
    Drafts = 'drafts',
    Campaigns = 'campaigns',
}

export enum CampaignsActionTypes {
    LOAD_BY_TARGET_START = '@@campaigns/LOAD_BY_TARGET_START',
    LOAD_BY_TARGET_ERROR = '@@campaigns/LOAD_BY_TARGET_ERROR',
    LOAD_BY_TARGET_SUCCESS = '@@campaigns/LOAD_BY_TARGET_SUCCESS',
    SET_CAMPAIGN_CREATOR_OPEN = '@@campaigns/SET_CAMPAIGN_CREATOR_OPEN',
    LOAD_ALL = '@@campaigns/LOAD_ALL',
    LOAD_ALL_ERROR = '@@campaigns/LOAD_ALL_ERROR',
    LOAD_ALL_SUCCESS = '@@campaigns/LOAD_ALL_SUCCESS',
    EXPORT_AUTOMATION_CONTACTS = '@@campaigns/EXPORT_AUTOMATION_CONTACTS',
    GET_LOCATION_STATS_BY_COUNTRYISO_START = '@@reports/GET_LOCATION_STATS_BY_COUNTRYISO_START',
    GET_LOCATION_STATS_BY_COUNTRYISO_SUCCESS = '@@reports/GET_LOCATION_STATS_BY_COUNTRYISO_SUCCESS',
    UPDATE_PAGINATION = '@@campaigns/UPDATE_PAGINATION',
    OPEN_MODAL_DELETE_CAMPAIGNS = '@@campaigns/OPEN_MODAL_DELETE_CAMPAIGNS',
    DELETE_CAMPAIGNS_START = '@@campaigns/DELETE_CAMPAIGNS_START',
    DELETE_CAMPAIGNS_SUCCESS = '@@campaigns/DELETE_CAMPAIGNS_SUCCESS',
    PICK_CAMPAIGNS = '@@campaigns/PICK_CAMPAIGNS',
    POLLING_IN_PROGRESS_CAMPAIGN_BY_CHANNEL_NAME = '@@campaigns/POLLING_IN_PROGRESS_CAMPAIGN_BY_CHANNEL_NAME',
    GET_IN_PROGRESS_CAMPAIGN_BY_CHANNEL_NAME = '@@campaigns/GET_IN_PROGRESS_CAMPAIGN_BY_CHANNEL_NAME',
    GET_IN_PROGRESS_CAMPAIGN_BY_CHANNEL_NAME_SUCCESS = '@@campaigns/GET_IN_PROGRESS_CAMPAIGN_BY_CHANNEL_NAME_SUCCESS',
    CANCEL_POLLING_IN_PROGRESS_CAMPAIGN_BY_CHANNEL_NAME = '@@campaigns/CANCEL_POLLING_IN_PROGRESS_CAMPAIGN_BY_CHANNEL_NAME',
    UNPICK_CAMPAIGNS = '@@campaigns/UNPICK_CAMPAIGNS',
    PAUSE_AUTORESPONDER = '@@campaigns/PAUSE_AUTORESPONDER',
    REACTIVATE_AUTORESPONDER = '@@campaigns/REACTIVATE_AUTORESPONDER',
    CHANGE_AUTORESPONDER_STATUS = '@@campaigns/CHANGE_AUTORESPONDER_STATUS',
    CHANGE_AUTORESPONDER_STATUS_SUCCESS = '@@campaigns/CHANGE_AUTORESPONDER_STATUS_SUCCESS',
    CANCEL_CAMPAIGN = '@@campaigns/CANCEL_CAMPAIGN',
    CANCEL_CAMPAIGN_START = '@@campaigns/CANCEL_CAMPAIGN_START',
    CANCEL_CAMPAIGN_SUCCESS = '@@campaigns/CANCEL_CAMPAIGN_SUCCESS',
    PREVIEW_CAMPAIGN = '@@campaigns/PREVIEW_CAMPAIGN',
    PREVIEW_CAMPAIGN_CLEAR = '@@campaigns/PREVIEW_CAMPAIGN_CLEAR',
    LOAD_PREVIEW_TEMPLATES = '@@campaigns/LOAD_PREVIEW_TEMPLATES',
    LOAD_PREVIEW_TEMPLATES_SUCCESS = '@@campaigns/LOAD_PREVIEW_TEMPLATES_SUCCESS',
    LOAD_PREVIEW_TEMPLATES_ERROR = '@@campaigns/LOAD_PREVIEW_TEMPLATES_ERROR',
    BEGIN_POLLING_CAMPAIGN_SIMPLE_STATS = '@@campaigns/BEGIN_POLLING_CAMPAIGN_SIMPLE_STATS',
    GET_CAMPAIGN_BY_NAME = '@@campaigns/GET_CAMPAIGN_BY_NAME',
    POLLING_CAMPAIGN_SIMPLE_STATS_SUCCESS = '@@campaigns/POLLING_CAMPAIGN_SIMPLE_STATS_SUCCESS',
    CANCEL_POLLING_CAMPAIGN_SIMPLE_STATS = '@@campaigns/CANCEL_POLLING_CAMPAIGN_SIMPLE_STATS',
    CLEAR_PICKED_CAMAPAIGN = '@@campaigns/CLEAR_PICKED_CAMAPAIGN',
    GET_CAMPAIGN_BY_NAME_SUCCESS = '@@campaigns/GET_CAMPAIGN_BY_NAME_SUCCESS',
    GET_CAMPAIGN_REPORTS_SUMMARY = '@@campaigns/GET_CAMPAIGN_REPORTS_SUMMARY',
    GET_CAMPAIGN_CLICKS_AND_OPENS_CHART_DATA = '@@campaigns/GET_CAMPAIGN_CLICKS_AND_OPENS_CHART_DATA',
    GET_CAMPAIGN_CLICKS_AND_OPENS_CHART_DATA_SUCCESS = '@@campaigns/GET_CAMPAIGN_CLICKS_AND_OPENS_CHART_DATA_SUCCESS',
    GET_CAMPAIGN_SIMPLE_STATS = '@@campaigns/GET_CAMPAIGN_SIMPLE_STATS',
    GET_CAMPAIGN_SIMPLE_STATS_SUCCESS = '@@campaigns/GET_CAMPAIGN_SIMPLE_STATS_SUCCESS',
    GET_CAMPAIGN_SIMPLE_STATS_ALL_DATA_SUCCESS = '@@campaigns/GET_CAMPAIGN_SIMPLE_STATS_ALL_DATA_SUCCESS',
    SET_ABSPLIT_CAMPAIGN_TEMPLATES_LABELS = '@@campaigns/SET_ABSPLIT_CAMPAIGN_TEMPLATES_LABELS',
    EXPORT_CAMPAIGNS = '@@campaigns/EXPORT_CAMPAIGNS',
    SET_ABSPLIT_DISPLAY_MODE = '@@campaigns/SET_ABSPLIT_DISPLAY_MODE',
    SET_ABSPLIT_CURRENT_TEMPLATE = '@@campaigns/SET_ABSPLIT_CURRENT_TEMPLATE',
    SET_PICKED_CONTACT_EMAIL = '@@campaigns/SET_PICKED_CONTACT_EMAIL',
    CLOSE_MODAL_DELETE_CAMPAIGNS = '@@campaigns/CLOSE_MODAL_DELETE_CAMPAIGNS',
    COPY_CAMPAIGNS_BY_NAME_START = '@@campaigns/COPY_CAMPAIGNS_BY_NAME_START',
    COPY_CAMPAIGNS_BY_NAME_SUCCESS = '@@campaigns/COPY_CAMPAIGNS_BY_NAME_SUCCESS',
    COPY_CAMPAIGNS_BY_NAME_ERROR = '@@campaigns/COPY_CAMPAIGNS_BY_NAME_ERROR',
    CLEAR_CAMPAIGN_REPORTS_SUMMARY = '@@campaigns/CLEAR_CAMPAIGN_REPORTS_SUMMARY',
    TEMPLATE_DELETED_STATUS = '@@campaigns/TEMPLATE_DELETED_STATUS',
    TEMPLATE_DELETED_STATUS_ERROR = '@@campaigns/TEMPLATE_DELETED_STATUS_ERROR',
    GET_CAMPAIGNS = '@@campaigns/GET_CAMPAIGNS',
    UPDATE_CAMPAIGNS_STORE = '@@campaigns/UPDATE_CAMPAIGNS_STORE',
    CAMPAIGNS_ERROR = '@@campaigns/ERROR',
    CAMPAIGNS_NAMES = '@@campaigns/CAMPAIGNS_NAMES',
    CAMPAIGNS_NAMES_ERROR = '@@campaigns/CAMPAIGNS_NAMES_ERROR',
    UPDATE_CAMPAIGNS_NAMES = '@@campaigns/UPDATE_CAMPAIGNS_NAMES',
}

export interface CampaignsByTargetFetchStartAction extends Action {
    type: CampaignsActionTypes.LOAD_BY_TARGET_START;
    payload: {
        loading: boolean;
        params: CampaignsByTargetInterface;
    };
}

export interface CampaignsByTargetFetchErrorAction extends Action {
    type: CampaignsActionTypes.LOAD_BY_TARGET_ERROR;
    payload: {
        loading: boolean;
    };
}

export interface CampaignsByTargetFetchSuccessAction extends Action {
    type: CampaignsActionTypes.LOAD_BY_TARGET_SUCCESS;
    payload: {
        loading: boolean;
        statusCount: CampaignStatusCounts;
        byTarget: CampaignChannel[];
    };
}

export interface GetCampaingsNamesAction extends Action {
    type: CampaignsActionTypes.CAMPAIGNS_NAMES;
    payload: CampaignTypeFilter[];
}

export interface GetUpdateCampaignsNamesAction extends Action {
    type: CampaignsActionTypes.UPDATE_CAMPAIGNS_NAMES;
    payload: {
        campaignsNames: string[];
    };
}

export interface GetCampaingsNamesErrorAction extends Action {
    type: CampaignsActionTypes.CAMPAIGNS_NAMES_ERROR;
}

export interface SetCampaignCreatorOpenAction extends Action {
    type: CampaignsActionTypes.SET_CAMPAIGN_CREATOR_OPEN;
    payload: {
        isCampaignCreatorOpen: boolean;
        contactsList: string;
        templateName: string;
    };
}

export interface LocationCountryStatsInterface {
    loading?: boolean;
    totalCount?: number;
    dataSetByCountry?: LocationDetails[];
}

export interface GetCampaignsPayload {
    search?: string;
    from?: string;
    to?: string;
    status?: CampaignStatus[];
    type?: CampaignTypeFilter[];
    dateFilterBy?: CampaignDateFilterBy;
    offset?: number;
    limit?: number;
    isUrlUpdateDisabled?: boolean;
}

export interface GetCampaignsAction extends Action {
    type: CampaignsActionTypes.LOAD_ALL;
    payload: GetCampaignsPayload;
}

export interface GetCampaignsErrorAction extends Action {
    type: CampaignsActionTypes.LOAD_ALL_ERROR;
}

export interface GetCampaignsSuccessAction extends Action {
    type: CampaignsActionTypes.LOAD_ALL_SUCCESS;
    payload: {
        collection: Campaign[];
        statusCount: CampaignStatusCounts;
    };
}

export interface GetLocationStatsByCountryIsoStartAction extends Action {
    type: CampaignsActionTypes.GET_LOCATION_STATS_BY_COUNTRYISO_START;
    payload: LocationStatsByCountryIsoInterface;
}
export interface GetLocationStatsByCountryIsoSuccessAction extends Action {
    type: CampaignsActionTypes.GET_LOCATION_STATS_BY_COUNTRYISO_SUCCESS;
    payload: { params: LocationCountryStatsInterface; loading: boolean };
}
export interface UpdateCampaignsPaginationAction extends Action {
    type: CampaignsActionTypes.UPDATE_PAGINATION;
    payload: {
        pagination: PaginationInterface;
    };
}

export interface DeleteCampaignsAction extends Action {
    type: CampaignsActionTypes.OPEN_MODAL_DELETE_CAMPAIGNS;
    payload: {
        campaignNames: string[];
        type: CampaignTypeFilter[];
    };
}

export interface DeleteCampaignsStartAction extends Action {
    type: CampaignsActionTypes.DELETE_CAMPAIGNS_START;
    payload: {
        campaignNames: string[];
        type: CampaignTypeFilter[];
    };
}

export interface DeleteCampaignsSuccessAction extends Action {
    type: CampaignsActionTypes.DELETE_CAMPAIGNS_SUCCESS;
}

export interface PickCampaignsAction extends Action {
    type: CampaignsActionTypes.PICK_CAMPAIGNS;
    payload: {
        checkedCampaigns: string[];
    };
}
export interface CampaignInProgressPollingAction extends Action {
    type: CampaignsActionTypes.POLLING_IN_PROGRESS_CAMPAIGN_BY_CHANNEL_NAME;
    payload: {
        loading: boolean;
        channelName: string;
    };
}
export interface CampaignInProgressAction extends Action {
    type: CampaignsActionTypes.GET_IN_PROGRESS_CAMPAIGN_BY_CHANNEL_NAME;
    payload: {
        loading: boolean;
        channelName: string;
    };
}

export interface CampaignInProgressSuccessAction extends Action {
    type: CampaignsActionTypes.GET_IN_PROGRESS_CAMPAIGN_BY_CHANNEL_NAME_SUCCESS;
    payload: {
        loading: boolean;
        inProgressCampaign: InProgressChannelStats;
    };
}

export interface CancelPollingInProgressCampaignByChannelNameAction extends Action {
    type: CampaignsActionTypes.CANCEL_POLLING_IN_PROGRESS_CAMPAIGN_BY_CHANNEL_NAME;
    payload: {
        loading: boolean;
        inProgressCampaign: null;
    };
}

export interface UnpickAllCampaignsAction extends Action {
    type: CampaignsActionTypes.UNPICK_CAMPAIGNS;
}

export interface PauseAutoresponderAction extends Action {
    type: CampaignsActionTypes.PAUSE_AUTORESPONDER;
    payload: {
        campaign: Campaign;
    };
}

export interface ReactivateAutoresponderAction extends Action {
    type: CampaignsActionTypes.REACTIVATE_AUTORESPONDER;
    payload: {
        campaign: Campaign;
    };
}

export interface ChangeAutoresponderStatusAction extends Action {
    type: CampaignsActionTypes.CHANGE_AUTORESPONDER_STATUS;
    payload: {
        campaign: Campaign;
        status: CampaignStatus;
    };
}

export interface ChangeAutoresponderStatusSuccessAction extends Action {
    type: CampaignsActionTypes.CHANGE_AUTORESPONDER_STATUS_SUCCESS;
    payload: {
        collection: Campaign[];
    };
}

export interface CancelCampaignAction extends Action {
    type: CampaignsActionTypes.CANCEL_CAMPAIGN;
    payload: {
        campaign: Campaign;
    };
}

export interface CancelCampaignStartAction extends Action {
    type: CampaignsActionTypes.CANCEL_CAMPAIGN_START;
    payload: {
        campaign: Campaign;
    };
}

export interface CancelCampaignSuccessAction extends Action {
    type: CampaignsActionTypes.CANCEL_CAMPAIGN_SUCCESS;
    payload: {
        collection: Campaign[];
    };
}

export interface PreviewCampaignAction extends Action {
    type: CampaignsActionTypes.PREVIEW_CAMPAIGN;
    payload: {
        campaign: Campaign;
        isWithTestResult?: boolean;
    };
}

export interface ClearPreviewCampaignAction extends Action {
    type: CampaignsActionTypes.PREVIEW_CAMPAIGN_CLEAR;
    payload: {
        campaign: null;
        templates: null;
    };
}

export interface TemplateNameInterface {
    displayName: string;
    backendName: string;
    channelName: string;
}

export interface LoadPreviewTemplatesAction extends Action {
    type: CampaignsActionTypes.LOAD_PREVIEW_TEMPLATES;
    payload: {
        names: TemplateNameInterface[];
    };
}

export interface LoadPreviewTemplatesSuccessAtion extends Action {
    type: CampaignsActionTypes.LOAD_PREVIEW_TEMPLATES_SUCCESS;
    payload: {
        templates: CampaignTemplateInterface[];
    };
}

export interface LoadPreviewTemplatesErrorAction extends Action {
    type: CampaignsActionTypes.LOAD_PREVIEW_TEMPLATES_ERROR;
}

export interface BeginPollingCampaignSimpleStatsAction extends Action {
    type: CampaignsActionTypes.BEGIN_POLLING_CAMPAIGN_SIMPLE_STATS;
    payload: {
        name: string;
        from?: string;
        to?: string;
    };
}

export interface GetCampaignByNameActionParams {
    name: string;
    campaignType: CampaignType;
    from?: string;
    to?: string;
    isLoadReportsRequired?: boolean;
    isLoadTemplatesRequired?: boolean;
    isWithTestResult?: boolean;
    isAbSplitClickAndOpenTestResultsRequired?: boolean;
    isLoadLinkTrackingRequired?: boolean;
    isSimpleStatsRequired?: boolean;
    isLinkTrackingAbSplitRequired?: boolean;
    abSplitOpenAndClickTestResultsOptions?: {
        minutesFromStart?: number;
    };
}

export interface GetCampaignByNameAction extends Action {
    type: CampaignsActionTypes.GET_CAMPAIGN_BY_NAME;
    payload: GetCampaignByNameActionParams;
}

export interface PollingCampaignSimpleStatsSuccessAction extends Action {
    type: CampaignsActionTypes.POLLING_CAMPAIGN_SIMPLE_STATS_SUCCESS;
    payload: CampaignSimpleStats;
}

export interface CancelPollingCampaignSimpleStatsAction extends Action {
    type: CampaignsActionTypes.CANCEL_POLLING_CAMPAIGN_SIMPLE_STATS;
}

export interface ClearPickedCampaignAction extends Action {
    type: CampaignsActionTypes.CLEAR_PICKED_CAMAPAIGN;
}

export interface GetCampaignByNameSuccessAction extends Action {
    type: CampaignsActionTypes.GET_CAMPAIGN_BY_NAME_SUCCESS;
    payload: {
        pickedCampaign: PickedCampaignInterface;
    };
}

export interface GetCampaignReportSummaryActionParams {
    /**
     * if possible pass campaign
     */
    campaign?: Campaign;
    /**
     * if campaign object is unavailable campaignName must be provided
     */
    campaignName?: string;
    /**
     * if campaign object is unavailable campaignType must be provided
     */
    campaignType?: CampaignType;
    /**
     * From date to pass as a GetReportSummary argument
     * @default campaign.TriggerOptions.DateToSendOn || campaign.DateCreated
     */
    from?: string;
    /**
     * To date to pass as a GetReportSummary argument
     * @default campaignType === Automated ? 2 days later than from : now
     */
    to?: string;
    /**
     * Interval of retrieved SummaryReport
     */
    interval?: IntervalType;
}

export interface GetCampaignReportsSummaryAction extends Action {
    type: CampaignsActionTypes.GET_CAMPAIGN_REPORTS_SUMMARY;
    payload: GetCampaignReportSummaryActionParams;
}

export interface ReportsClicksandopensByChannelnameGetParams {
    channelName: string;
    from: string;
    to: string;
    interval?: IntervalType;
    onlyChildCampaigns?: boolean;
    options?: any;
}

export interface GetCampaignClicksAndOpensByNameAction extends Action {
    type: CampaignsActionTypes.GET_CAMPAIGN_CLICKS_AND_OPENS_CHART_DATA;
    payload: ReportsClicksandopensByChannelnameGetParams;
}

export interface GetCampaignClicksAndOpensByNameActionSuccess extends Action {
    type: CampaignsActionTypes.GET_CAMPAIGN_CLICKS_AND_OPENS_CHART_DATA_SUCCESS;
    payload: {
        clicksAndOpensChartData: ChannelChartData[];
    };
}

export interface GetCampaignSimpleStatsAction extends Action {
    type: CampaignsActionTypes.GET_CAMPAIGN_SIMPLE_STATS;
    payload: GetCampaignByNameActionParams;
}

export interface GetCampaignSimpleStatsSuccessAction extends Action {
    type: CampaignsActionTypes.GET_CAMPAIGN_SIMPLE_STATS_SUCCESS;
    payload: CampaignSimpleStats;
}

export interface SetTemplateVersionLabelsAction extends Action {
    type: CampaignsActionTypes.SET_ABSPLIT_CAMPAIGN_TEMPLATES_LABELS;
    payload: {
        [key: string]: string;
    };
}

export interface ExportCampaignsAction extends Action {
    type: CampaignsActionTypes.EXPORT_CAMPAIGNS;
    payload: ExportCampaignsParams;
}

export interface ExportAutomationContactsAction extends Action {
    type: CampaignsActionTypes.EXPORT_AUTOMATION_CONTACTS;
    payload: ExportAutomationParams;
}

export interface SetAbSplitDisplayModeAction extends Action {
    type: CampaignsActionTypes.SET_ABSPLIT_DISPLAY_MODE;
    payload: {
        displayMode: AbSplitDisplayMode;
    };
}

export interface SetAbSplitCurrentTemplateAction extends Action {
    type: CampaignsActionTypes.SET_ABSPLIT_CURRENT_TEMPLATE;
    payload: {
        currentTemplate: string;
    };
}

export interface SetPickedContactEmailAction extends Action {
    type: CampaignsActionTypes.SET_PICKED_CONTACT_EMAIL;
    payload: {
        pickedContactEmail: string;
    };
}

export interface CloseDeleteCampaignModalAction extends Action {
    type: CampaignsActionTypes.CLOSE_MODAL_DELETE_CAMPAIGNS;
    payload: {
        isOpened: boolean;
        campaignNames: string[];
        type: CampaignType;
    };
}

export interface CopyCampaignsByNameStartAction extends Action {
    type: CampaignsActionTypes.COPY_CAMPAIGNS_BY_NAME_START;
    payload: {
        name: string;
    };
}

export interface CopyCampaignsByNameSuccessAction extends Action {
    type: CampaignsActionTypes.COPY_CAMPAIGNS_BY_NAME_SUCCESS;
    payload: {
        copiedCampaign: Campaign;
    };
}

export interface TemplateDeletedStatusAction extends Action {
    type: CampaignsActionTypes.TEMPLATE_DELETED_STATUS;
}

export interface TemplateDeletedStatusErrorAction extends Action {
    type: CampaignsActionTypes.TEMPLATE_DELETED_STATUS_ERROR;
}

export interface CopyCampaignsByNameErrorAction extends Action {
    type: CampaignsActionTypes.COPY_CAMPAIGNS_BY_NAME_ERROR;
}

export type WinnerResultsTableType = {
    pickedCampaign: PickedCampaignInterface;
    advancedOptions: AdvancedOptions;
};

export type LineChartType = {
    type: CampaignType;
    AdvancedOptions: AdvancedOptions;
    isOpensTracked: boolean;
    isClicksTracked: boolean;
    isLinkTracked: boolean;
    reports: ReportsState;
    campaigns: CampaignsInterface;
};

export type CreateListType = {
    decodedName: string;
    campaign: Campaign;
};

export type SendCampaignInfoType = {
    label: JSX.Element;
    value: JSX.Element;
};

export type PerformanceStatsType = {
    isClicksTracked: boolean;

    isOpensTracked: boolean;
    campaigns: CampaignsInterface;
};
